import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as analyticsApi from '../../api/analytics';
import { generateTraceId, logAction } from '../../utils/trace';

// =============================================================================
// SYNC CONFIG ASYNC THUNKS
// =============================================================================

export const fetchSyncObjects = createAsyncThunk(
  'analytics/fetchSyncObjects',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();
    const dataSourceId = args.dataSourceId || null;
    try {
      logAction({ type: 'analytics/fetchSyncObjects/pending', meta: { traceId } }, {});
      const data = await analyticsApi.fetchSyncObjects(dataSourceId);
      logAction({ type: 'analytics/fetchSyncObjects/fulfilled', meta: { traceId } }, {
        objectCount: data?.length || 0,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/fetchSyncObjects/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSyncObjectFields = createAsyncThunk(
  'analytics/fetchSyncObjectFields',
  async ({ objectName, dataSourceId = null, storeKey = null, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'analytics/fetchSyncObjectFields/pending', meta: { traceId } }, {
        objectName,
      });
      const data = await analyticsApi.fetchSyncObjectFields(objectName, dataSourceId);
      logAction({ type: 'analytics/fetchSyncObjectFields/fulfilled', meta: { traceId } }, {
        objectName,
        storeKey: storeKey || objectName,
        fieldCount: data?.length || 0,
      });
      // storeKey is the qualified name (qualified_name || objectName) used as the Redux map key.
      // objectName is the raw CRM name used for API calls.
      return { objectName, storeKey: storeKey || objectName, fields: data };
    } catch (error) {
      logAction({ type: 'analytics/fetchSyncObjectFields/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const saveSyncChanges = createAsyncThunk(
  'analytics/saveSyncChanges',
  async (dataSourceId = null, { getState, rejectWithValue }) => {
    const traceId = generateTraceId();
    try {
      const { syncConfig } = getState().analytics;
      const add = Object.values(syncConfig.pendingChanges.add);
      // pendingChanges.remove contains qualified keys; map back to raw object names for the API.
      const removeKeys = syncConfig.pendingChanges.remove;
      const objects = syncConfig.objects;
      const removeNames = removeKeys.map(key => {
        const obj = objects.find(o => (o.qualified_name || o.name) === key);
        return obj ? obj.name : key;
      });

      logAction({ type: 'analytics/saveSyncChanges/pending', meta: { traceId } }, {
        addCount: add.length,
        removeCount: removeNames.length,
      });

      const result = await analyticsApi.updateSyncObjects(add, removeNames, dataSourceId);

      // Also update field selections if any.
      // fieldChanges values are { objectName, dataSourceId, fields } objects.
      const fieldChanges = syncConfig.pendingChanges.fieldChanges;
      for (const [, change] of Object.entries(fieldChanges)) {
        const { objectName, dataSourceId: fieldDs, fields } = change;
        await analyticsApi.updateSyncObjectFields(objectName, 'replace', fields, fieldDs || dataSourceId);
      }

      logAction({ type: 'analytics/saveSyncChanges/fulfilled', meta: { traceId } }, {
        added: result.added?.length || 0,
        removed: result.removed?.length || 0,
      });

      return result;
    } catch (error) {
      logAction({ type: 'analytics/saveSyncChanges/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const triggerMetadataRefresh = createAsyncThunk(
  'analytics/triggerMetadataRefresh',
  async ({ objectNames = [], dataSourceId = null, traceId: inputTraceId } = {}, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'analytics/triggerMetadataRefresh/pending', meta: { traceId } }, {
        objectNames,
      });
      const data = await analyticsApi.refreshSyncMetadata(objectNames, dataSourceId);
      logAction({ type: 'analytics/triggerMetadataRefresh/fulfilled', meta: { traceId } }, {
        workflowId: data.workflow_id,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/triggerMetadataRefresh/rejected', meta: { traceId } }, {
        error: error.message,
        code: error.data?.code,
      });
      // Return the full error data for auth_required errors
      if (error.data?.code === 'auth_required') {
        return rejectWithValue(error.data);
      }
      return rejectWithValue({ message: error.message });
    }
  }
);

export const pollSyncWorkflowProgress = createAsyncThunk(
  'analytics/pollSyncWorkflowProgress',
  async ({ workflowId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      const data = await analyticsApi.getSyncWorkflowProgress(workflowId);
      logAction({ type: 'analytics/pollSyncWorkflowProgress/fulfilled', meta: { traceId } }, {
        workflowId,
        status: data.status,
        progressPercent: data.progress_percent,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/pollSyncWorkflowProgress/rejected', meta: { traceId } }, {
        workflowId,
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

// Async thunks
export const fetchFields = createAsyncThunk(
  'analytics/fetchFields',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();
    try {
      logAction({ type: 'analytics/fetchFields/pending', meta: { traceId } }, {});
      const data = await analyticsApi.fetchFields();
      logAction({ type: 'analytics/fetchFields/fulfilled', meta: { traceId } }, {
        objectCount: data.objects?.length || 0,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/fetchFields/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchFieldsByObject = createAsyncThunk(
  'analytics/fetchFieldsByObject',
  async ({ objectName, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'analytics/fetchFieldsByObject/pending', meta: { traceId } }, {
        objectName,
      });
      const data = await analyticsApi.fetchFieldsByObject(objectName);
      logAction({ type: 'analytics/fetchFieldsByObject/fulfilled', meta: { traceId } }, {
        objectName,
        fieldCount: data.fields?.length || 0,
      });
      return { objectName, data };
    } catch (error) {
      logAction({ type: 'analytics/fetchFieldsByObject/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchDependencies = createAsyncThunk(
  'analytics/fetchDependencies',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();
    try {
      logAction({ type: 'analytics/fetchDependencies/pending', meta: { traceId } }, {});
      const data = await analyticsApi.fetchDependencies();
      logAction({ type: 'analytics/fetchDependencies/fulfilled', meta: { traceId } }, {
        dependencyCount: data.dependencies?.length || 0,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/fetchDependencies/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSyncConfigs = createAsyncThunk(
  'analytics/fetchSyncConfigs',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();
    try {
      logAction({ type: 'analytics/fetchSyncConfigs/pending', meta: { traceId } }, {});
      const data = await analyticsApi.fetchSyncConfigs();
      logAction({ type: 'analytics/fetchSyncConfigs/fulfilled', meta: { traceId } }, {
        configCount: data.configs?.length || 0,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/fetchSyncConfigs/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const createSyncConfig = createAsyncThunk(
  'analytics/createSyncConfig',
  async ({ config, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'analytics/createSyncConfig/pending', meta: { traceId } }, {
        configType: config.type,
      });
      const data = await analyticsApi.createSyncConfig(config);
      logAction({ type: 'analytics/createSyncConfig/fulfilled', meta: { traceId } }, {
        configId: data.config_id,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/createSyncConfig/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSyncConfig = createAsyncThunk(
  'analytics/deleteSyncConfig',
  async ({ configId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'analytics/deleteSyncConfig/pending', meta: { traceId } }, {
        configId,
      });
      await analyticsApi.deleteSyncConfig(configId);
      logAction({ type: 'analytics/deleteSyncConfig/fulfilled', meta: { traceId } }, {
        configId,
      });
      return configId;
    } catch (error) {
      logAction({ type: 'analytics/deleteSyncConfig/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const runQuery = createAsyncThunk(
  'analytics/runQuery',
  async ({ query, traceId: inputTraceId, dataSourceId = null }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'analytics/runQuery/pending', meta: { traceId } }, {
        queryLength: query.length,
      });
      const data = await analyticsApi.runQuery(query, dataSourceId);
      logAction({ type: 'analytics/runQuery/fulfilled', meta: { traceId } }, {
        rowCount: data.rows?.length || 0,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/runQuery/rejected', meta: { traceId } }, {
        error: error.message,
      });
      // Pass structured error data so the UI can detect schema_stale (422) distinctly.
      return rejectWithValue(error.data || { message: error.message });
    }
  }
);

export const triggerFieldSync = createAsyncThunk(
  'analytics/triggerFieldSync',
  async ({ objectNames = [], dataSourceId = null, traceId: inputTraceId } = {}, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      logAction({ type: 'analytics/triggerFieldSync/pending', meta: { traceId } }, {
        objectNames,
        dataSourceId,
      });
      const data = await analyticsApi.triggerFieldSync(objectNames, dataSourceId);
      logAction({ type: 'analytics/triggerFieldSync/fulfilled', meta: { traceId } }, {
        workflowId: data.workflow_id,
        runId: data.run_id,
        tenantId: data.tenant_id,
        status: data.status,
      });
      return data;
    } catch (error) {
      const errorDetails = {
        message: error.message,
        status: error.status,
        traceId: error.traceId || traceId,
      };
      console.error('[Analytics] triggerFieldSync failed:', errorDetails);
      logAction({ type: 'analytics/triggerFieldSync/rejected', meta: { traceId } }, errorDetails);
      return rejectWithValue(error.message || 'Failed to trigger field sync');
    }
  }
);

export const fetchSyncStatus = createAsyncThunk(
  'analytics/fetchSyncStatus',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();
    try {
      logAction({ type: 'analytics/fetchSyncStatus/pending', meta: { traceId } }, {});
      const data = await analyticsApi.fetchSyncStatus();
      logAction({ type: 'analytics/fetchSyncStatus/fulfilled', meta: { traceId } }, {
        connected: data.salesforce_connected,
        status: data.salesforce_status,
        hasSyncedData: data.has_synced_data,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/fetchSyncStatus/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchWorkflowStatus = createAsyncThunk(
  'analytics/fetchWorkflowStatus',
  async ({ workflowId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();
    try {
      const data = await analyticsApi.fetchWorkflowStatus(workflowId);
      logAction({ type: 'analytics/fetchWorkflowStatus/fulfilled', meta: { traceId } }, {
        workflowId,
        status: data.status,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/fetchWorkflowStatus/rejected', meta: { traceId } }, {
        workflowId,
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

// =============================================================================
// TENANT SYNC SETTINGS & INCREMENTAL SYNC THUNKS
// =============================================================================

export const fetchSyncSettings = createAsyncThunk(
  'analytics/fetchSyncSettings',
  async (_, { rejectWithValue }) => {
    const traceId = generateTraceId();
    try {
      logAction({ type: 'analytics/fetchSyncSettings/pending', meta: { traceId } }, {});
      const data = await analyticsApi.fetchSyncSettings();
      logAction({ type: 'analytics/fetchSyncSettings/fulfilled', meta: { traceId } }, {
        frequency: data.default_sync_frequency,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/fetchSyncSettings/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const updateSyncFrequency = createAsyncThunk(
  'analytics/updateSyncFrequency',
  async (frequency, { rejectWithValue }) => {
    const traceId = generateTraceId();
    try {
      logAction({ type: 'analytics/updateSyncFrequency/pending', meta: { traceId } }, { frequency });
      await analyticsApi.updateSyncSettings(frequency);
      logAction({ type: 'analytics/updateSyncFrequency/fulfilled', meta: { traceId } }, { frequency });
      return { frequency };
    } catch (error) {
      logAction({ type: 'analytics/updateSyncFrequency/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const triggerSyncNow = createAsyncThunk(
  'analytics/triggerSyncNow',
  async (dataSourceId = null, { rejectWithValue }) => {
    const traceId = generateTraceId();
    try {
      logAction({ type: 'analytics/triggerSyncNow/pending', meta: { traceId } }, {});
      const data = await analyticsApi.triggerSyncNow(dataSourceId);
      logAction({ type: 'analytics/triggerSyncNow/fulfilled', meta: { traceId } }, {
        status: data.status,
        workflowId: data.workflow_id,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/triggerSyncNow/rejected', meta: { traceId } }, {
        error: error.message,
        code: error.data?.code,
      });
      // Return the full error data for auth_required errors
      if (error.data?.code === 'auth_required') {
        return rejectWithValue(error.data);
      }
      return rejectWithValue({ message: error.message });
    }
  }
);

export const fetchFullSyncStatus = createAsyncThunk(
  'analytics/fetchFullSyncStatus',
  async (_, { rejectWithValue }) => {
    const traceId = generateTraceId();
    try {
      const data = await analyticsApi.fetchFullSyncStatus();
      logAction({ type: 'analytics/fetchFullSyncStatus/fulfilled', meta: { traceId } }, {
        hasActiveWorkflow: !!data.active_workflow,
        objectCount: data.objects?.length || 0,
      });
      return data;
    } catch (error) {
      logAction({ type: 'analytics/fetchFullSyncStatus/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

// =============================================================================
// BUILDER V2 THUNKS — relationship-aware tree builder
// =============================================================================

export const fetchBuilderLookups = createAsyncThunk(
  'analytics/fetchBuilderLookups',
  async ({ objectName }, { rejectWithValue }) => {
    try {
      const data = await analyticsApi.fetchLookupRelationships(objectName);
      return { objectName, lookups: data?.relationships || [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBuilderChildren = createAsyncThunk(
  'analytics/fetchBuilderChildren',
  async ({ objectName }, { rejectWithValue }) => {
    try {
      const data = await analyticsApi.fetchChildRelationships(objectName);
      return { objectName, children: data?.relationships || [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState: {
    // Field explorer state
    objects: [],
    selectedObject: null,
    objectFields: {},

    // Dependencies state
    dependencies: null,
    orphanFields: [],
    phantomFields: [],

    // Sync configs state
    syncConfigs: [],

    // Loading states
    isLoadingFields: false,
    isLoadingDependencies: false,
    isLoadingSyncConfigs: false,
    isTriggeringSync: false,

    // Initial load tracking - tracks each request separately
    initialLoadComplete: false,
    initialLoadStatus: {
      fields: 'pending',       // pending, loading, success, error
      syncStatus: 'pending',
      fullSyncStatus: 'pending',
      fieldsError: null,
      syncStatusError: null,
      fullSyncStatusError: null,
    },

    // Sync workflow tracking
    activeSyncWorkflow: null,

    // Per-data-source auth status: { [dataSourceId]: { connected: bool, status: string } }
    authByDataSource: {},
    isLoadingSyncStatus: false,

    // Errors
    error: null,

    // UI state
    activeTab: 'syncConfig',
    searchQuery: '',

    // Query state
    queryText: '',
    queryResult: null,
    queryColumns: [],
    queryRows: [],
    queryError: null,
    isRunningQuery: false,
    queryHistory: [],

    // =============================================================================
    // SYNC CONFIGURATION STATE
    // =============================================================================
    syncConfig: {
      // Cached list from backend
      objects: [],
      isLoadingObjects: false,

      // Selected object for field editing
      selectedObject: null,
      objectFields: {},  // keyed by object name
      isLoadingFields: false,

      // Pending changes (before save)
      pendingChanges: {
        add: {},        // { [objectName]: ObjectSyncConfig }
        remove: [],     // string[]
        fieldChanges: {},  // { [objectName]: string[] }
      },
      hasUnsavedChanges: false,

      // Workflow progress
      activeWorkflow: null,

      // Operation states
      isSaving: false,
      isRefreshing: false,
      error: null,
    },

    // Active topic job (clustering or embedding) — shown in TopicProgressPanel
    // Restored from localStorage on init so it survives page refreshes
    activeTopicJob: (() => {
      try {
        const saved = localStorage.getItem('activeTopicJob');
        if (saved) {
          const job = JSON.parse(saved);
          // Only restore running jobs; completed/failed are transient
          return job?.status === 'running' ? job : null;
        }
      } catch (_) {}
      return null;
    })(),

    // Builder V2 relationship state
    builderRelationships: {
      lookup: [],    // LookupRelationshipInfo[] for selected object
      children: [],  // ChildRelationshipInfo[] for selected object
      isLoading: false,
      error: null,
    },

    // Tenant sync settings
    tenantSync: {
      frequency: 'daily', // hourly, daily, weekly, disabled
      lastTenantSyncAt: null,
      lastTenantSyncStatus: null,
      isLoadingSettings: false,
      isUpdatingFrequency: false,
      isTriggeringSyncNow: false,
      syncNowStatus: null, // 'started', 'already_running'
      syncNowWorkflowId: null,
      error: null,
      // Auth required modal state
      authRequired: null, // { source_type, source_name, data_source_id, auth_action }
    },
  },
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setSelectedObject: (state, action) => {
      state.selectedObject = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetInitialLoad: (state) => {
      state.initialLoadComplete = false;
      state.initialLoadStatus = {
        fields: 'pending',
        syncStatus: 'pending',
        fullSyncStatus: 'pending',
        fieldsError: null,
        syncStatusError: null,
        fullSyncStatusError: null,
      };
      state.error = null;
    },
    setQueryText: (state, action) => {
      state.queryText = action.payload;
    },
    clearQueryResult: (state) => {
      state.queryResult = null;
      state.queryColumns = [];
      state.queryRows = [];
      state.queryError = null;
    },
    clearActiveSyncWorkflow: (state) => {
      state.activeSyncWorkflow = null;
    },
    setActiveTopicJob: (state, action) => {
      state.activeTopicJob = action.payload;
      try { localStorage.setItem('activeTopicJob', JSON.stringify(action.payload)); } catch (_) {}
    },
    clearActiveTopicJob: (state) => {
      state.activeTopicJob = null;
      try { localStorage.removeItem('activeTopicJob'); } catch (_) {}
    },
    clearBuilderRelationships: (state) => {
      state.builderRelationships = { lookup: [], children: [], isLoading: false, error: null };
    },
    // =============================================================================
    // SYNC CONFIG REDUCERS
    // =============================================================================
    // toggleObjectSync uses objectKey (qualified_name || name) as the state map key.
    // config.object_name holds the raw CRM name for API calls.
    toggleObjectSync: (state, action) => {
      const { objectName: objectKey, config } = action.payload;
      const { pendingChanges } = state.syncConfig;

      if (config) {
        // Adding object - remove from remove list if present
        pendingChanges.remove = pendingChanges.remove.filter(k => k !== objectKey);
        pendingChanges.add[objectKey] = config;
      } else {
        // Removing object - remove from add list if present
        delete pendingChanges.add[objectKey];
        if (!pendingChanges.remove.includes(objectKey)) {
          pendingChanges.remove.push(objectKey);
        }
      }

      state.syncConfig.hasUnsavedChanges =
        Object.keys(pendingChanges.add).length > 0 ||
        pendingChanges.remove.length > 0 ||
        Object.keys(pendingChanges.fieldChanges).length > 0;
    },
    // Revert pending changes for an object (restore to original synced/not-synced state)
    // objectKey = qualified_name || name
    revertObjectSync: (state, action) => {
      const objectKey = action.payload;
      const { pendingChanges } = state.syncConfig;

      // Remove from both add and remove lists
      delete pendingChanges.add[objectKey];
      pendingChanges.remove = pendingChanges.remove.filter(k => k !== objectKey);
      delete pendingChanges.fieldChanges[objectKey];

      state.syncConfig.hasUnsavedChanges =
        Object.keys(pendingChanges.add).length > 0 ||
        pendingChanges.remove.length > 0 ||
        Object.keys(pendingChanges.fieldChanges).length > 0;
    },
    // updatePendingFields stores field changes keyed by the qualified key.
    // objectName is the raw CRM name; dataSourceId scopes the backend update.
    updatePendingFields: (state, action) => {
      const { objectKey, objectName, dataSourceId = null, fields } = action.payload;
      const key = objectKey || objectName; // backwards compat
      state.syncConfig.pendingChanges.fieldChanges[key] = { objectName: objectName || key, dataSourceId, fields };
      state.syncConfig.hasUnsavedChanges = true;
    },
    discardSyncChanges: (state) => {
      state.syncConfig.pendingChanges = { add: {}, remove: [], fieldChanges: {} };
      state.syncConfig.hasUnsavedChanges = false;
    },
    setSyncConfigSelectedObject: (state, action) => {
      state.syncConfig.selectedObject = action.payload;
    },
    clearSyncConfigWorkflow: (state) => {
      state.syncConfig.activeWorkflow = null;
    },
    clearSyncConfigError: (state) => {
      state.syncConfig.error = null;
    },
    clearAuthRequired: (state) => {
      state.tenantSync.authRequired = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFields
      .addCase(fetchFields.pending, (state) => {
        state.isLoadingFields = true;
        state.error = null;
        state.initialLoadStatus.fields = 'loading';
        state.initialLoadStatus.fieldsError = null;
      })
      .addCase(fetchFields.fulfilled, (state, action) => {
        state.isLoadingFields = false;
        state.objects = action.payload.objects || [];
        state.initialLoadStatus.fields = 'success';
        // Only mark complete when all initial requests are done
        if (state.initialLoadStatus.syncStatus !== 'pending' && state.initialLoadStatus.syncStatus !== 'loading' &&
            state.initialLoadStatus.fullSyncStatus !== 'pending' && state.initialLoadStatus.fullSyncStatus !== 'loading') {
          state.initialLoadComplete = true;
        }
      })
      .addCase(fetchFields.rejected, (state, action) => {
        state.isLoadingFields = false;
        state.error = action.payload;
        state.initialLoadStatus.fields = 'error';
        state.initialLoadStatus.fieldsError = action.payload;
        // Mark complete even on error so user sees the error
        state.initialLoadComplete = true;
      })
      // fetchFieldsByObject
      .addCase(fetchFieldsByObject.pending, (state) => {
        state.isLoadingFields = true;
      })
      .addCase(fetchFieldsByObject.fulfilled, (state, action) => {
        state.isLoadingFields = false;
        state.objectFields[action.payload.objectName] = action.payload.data.fields || [];
      })
      .addCase(fetchFieldsByObject.rejected, (state, action) => {
        state.isLoadingFields = false;
        state.error = action.payload;
      })
      // fetchDependencies
      .addCase(fetchDependencies.pending, (state) => {
        state.isLoadingDependencies = true;
        state.error = null;
      })
      .addCase(fetchDependencies.fulfilled, (state, action) => {
        state.isLoadingDependencies = false;
        state.dependencies = action.payload.dependencies || [];
        state.orphanFields = action.payload.orphan_fields || [];
        state.phantomFields = action.payload.phantom_fields || [];
      })
      .addCase(fetchDependencies.rejected, (state, action) => {
        state.isLoadingDependencies = false;
        state.error = action.payload;
      })
      // fetchSyncConfigs
      .addCase(fetchSyncConfigs.pending, (state) => {
        state.isLoadingSyncConfigs = true;
      })
      .addCase(fetchSyncConfigs.fulfilled, (state, action) => {
        state.isLoadingSyncConfigs = false;
        state.syncConfigs = action.payload.configs || [];
      })
      .addCase(fetchSyncConfigs.rejected, (state, action) => {
        state.isLoadingSyncConfigs = false;
        state.error = action.payload;
      })
      // createSyncConfig
      .addCase(createSyncConfig.fulfilled, (state, action) => {
        state.syncConfigs.push(action.payload);
      })
      // deleteSyncConfig
      .addCase(deleteSyncConfig.fulfilled, (state, action) => {
        state.syncConfigs = state.syncConfigs.filter(c => c.config_id !== action.payload);
      })
      // runQuery
      .addCase(runQuery.pending, (state) => {
        state.isRunningQuery = true;
        state.queryError = null;
      })
      .addCase(runQuery.fulfilled, (state, action) => {
        state.isRunningQuery = false;
        state.queryResult = action.payload;
        state.queryColumns = action.payload.columns || [];
        state.queryRows = action.payload.rows || [];
        // Add to history
        if (state.queryText.trim()) {
          state.queryHistory = [
            { query: state.queryText, timestamp: new Date().toISOString() },
            ...state.queryHistory.slice(0, 9) // Keep last 10
          ];
        }
      })
      .addCase(runQuery.rejected, (state, action) => {
        state.isRunningQuery = false;
        state.queryError = action.payload;
        state.queryColumns = [];
        state.queryRows = [];
      })
      // triggerFieldSync
      .addCase(triggerFieldSync.pending, (state) => {
        state.isTriggeringSync = true;
        state.error = null;
      })
      .addCase(triggerFieldSync.fulfilled, (state, action) => {
        state.isTriggeringSync = false;
        state.activeSyncWorkflow = {
          workflowId: action.payload.workflow_id,
          runId: action.payload.run_id,
          status: action.payload.status,
          startedAt: new Date().toISOString(),
        };
      })
      .addCase(triggerFieldSync.rejected, (state, action) => {
        state.isTriggeringSync = false;
        state.error = action.payload;
      })
      // fetchSyncStatus
      .addCase(fetchSyncStatus.pending, (state) => {
        state.isLoadingSyncStatus = true;
        state.initialLoadStatus.syncStatus = 'loading';
        state.initialLoadStatus.syncStatusError = null;
      })
      .addCase(fetchSyncStatus.fulfilled, (state, action) => {
        state.isLoadingSyncStatus = false;
        // Populate authByDataSource from legacy API response.
        // The legacy endpoint returns salesforce_connected / salesforce_status;
        // store under a well-known legacy key so consumers can look it up.
        // When a data_source_id is available, prefer keying by that.
        const legacyKey = action.payload.data_source_id || '__legacy_sf__';
        state.authByDataSource[legacyKey] = {
          connected: !!action.payload.salesforce_connected,
          status: action.payload.salesforce_status || 'none',
        };
        state.initialLoadStatus.syncStatus = 'success';
        // Check if all initial loads are complete
        if (state.initialLoadStatus.fields !== 'pending' && state.initialLoadStatus.fields !== 'loading' &&
            state.initialLoadStatus.fullSyncStatus !== 'pending' && state.initialLoadStatus.fullSyncStatus !== 'loading') {
          state.initialLoadComplete = true;
        }
      })
      .addCase(fetchSyncStatus.rejected, (state, action) => {
        state.isLoadingSyncStatus = false;
        state.initialLoadStatus.syncStatus = 'error';
        state.initialLoadStatus.syncStatusError = action.payload || 'Failed to check data source status';
        // Mark complete even on error
        state.initialLoadComplete = true;
      })
      // fetchWorkflowStatus
      .addCase(fetchWorkflowStatus.fulfilled, (state, action) => {
        if (state.activeSyncWorkflow) {
          state.activeSyncWorkflow.status = action.payload.status;
          // Clear workflow if it's done (completed, failed, canceled, terminated)
          if (['completed', 'failed', 'canceled', 'terminated'].includes(action.payload.status)) {
            state.activeSyncWorkflow.completedAt = new Date().toISOString();
            if (action.payload.error) {
              state.activeSyncWorkflow.error = action.payload.error;
            }
          }
        }
      })
      // pollSyncWorkflowProgress - update both activeSyncWorkflow and syncConfig.activeWorkflow
      .addCase(pollSyncWorkflowProgress.fulfilled, (state, action) => {
        // Update activeSyncWorkflow (used by AnalyticsPage)
        if (state.activeSyncWorkflow) {
          state.activeSyncWorkflow = {
            ...state.activeSyncWorkflow,
            status: action.payload.status,
            current_step: action.payload.current_step,
            current_object: action.payload.current_object,
            objects_processed: action.payload.objects_processed,
            objects_total: action.payload.objects_total,
            records_processed: action.payload.records_processed,
            progress_percent: action.payload.progress_percent,
            object_progress: action.payload.object_progress,
            error: action.payload.error,
          };
          if (['completed', 'failed', 'canceled', 'terminated'].includes(action.payload.status)) {
            state.activeSyncWorkflow.completedAt = action.payload.completed_at || new Date().toISOString();
          }
        }
        // Update syncConfig.activeWorkflow (used by SyncProgressPanel)
        if (state.syncConfig.activeWorkflow) {
          state.syncConfig.activeWorkflow = {
            ...state.syncConfig.activeWorkflow,
            ...action.payload,
          };
        }
      })
      // =============================================================================
      // SYNC CONFIG EXTRA REDUCERS
      // =============================================================================
      // fetchSyncObjects
      .addCase(fetchSyncObjects.pending, (state) => {
        state.syncConfig.isLoadingObjects = true;
        state.syncConfig.error = null;
        // Clear stale field data from the previous data source so it doesn't bleed through
        state.syncConfig.objectFields = {};
      })
      .addCase(fetchSyncObjects.fulfilled, (state, action) => {
        state.syncConfig.isLoadingObjects = false;
        state.syncConfig.objects = action.payload || [];
      })
      .addCase(fetchSyncObjects.rejected, (state, action) => {
        state.syncConfig.isLoadingObjects = false;
        state.syncConfig.error = action.payload;
      })
      // fetchSyncObjectFields
      .addCase(fetchSyncObjectFields.pending, (state) => {
        state.syncConfig.isLoadingFields = true;
      })
      .addCase(fetchSyncObjectFields.fulfilled, (state, action) => {
        state.syncConfig.isLoadingFields = false;
        // Store fields keyed by storeKey (qualified_name || objectName) to avoid cross-source collisions.
        const key = action.payload.storeKey || action.payload.objectName;
        state.syncConfig.objectFields[key] = action.payload.fields || [];
      })
      .addCase(fetchSyncObjectFields.rejected, (state, action) => {
        state.syncConfig.isLoadingFields = false;
        state.syncConfig.error = action.payload;
      })
      // saveSyncChanges
      .addCase(saveSyncChanges.pending, (state) => {
        state.syncConfig.isSaving = true;
        state.syncConfig.error = null;
      })
      .addCase(saveSyncChanges.fulfilled, (state) => {
        state.syncConfig.isSaving = false;
        state.syncConfig.pendingChanges = { add: {}, remove: [], fieldChanges: {} };
        state.syncConfig.hasUnsavedChanges = false;
      })
      .addCase(saveSyncChanges.rejected, (state, action) => {
        state.syncConfig.isSaving = false;
        state.syncConfig.error = action.payload;
      })
      // triggerMetadataRefresh
      .addCase(triggerMetadataRefresh.pending, (state) => {
        state.syncConfig.isRefreshing = true;
        state.syncConfig.error = null;
      })
      .addCase(triggerMetadataRefresh.fulfilled, (state, action) => {
        state.syncConfig.isRefreshing = false;
        state.syncConfig.activeWorkflow = {
          workflow_id: action.payload.workflow_id,
          status: 'running',
          progress_percent: 0,
          current_step: 'Starting...',
          objects_processed: 0,
          objects_total: 0,
          started_at: new Date().toISOString(),
        };
      })
      .addCase(triggerMetadataRefresh.rejected, (state, action) => {
        state.syncConfig.isRefreshing = false;
        // Check if this is an auth_required error
        if (action.payload?.code === 'auth_required') {
          state.tenantSync.authRequired = {
            source_type: action.payload.source_type,
            source_name: action.payload.source_name,
            data_source_id: action.payload.data_source_id,
            auth_action: action.payload.auth_action,
          };
          state.syncConfig.error = null;
        } else {
          state.syncConfig.error = action.payload?.message || action.payload;
        }
      })
      // =============================================================================
      // TENANT SYNC SETTINGS EXTRA REDUCERS
      // =============================================================================
      // fetchSyncSettings
      .addCase(fetchSyncSettings.pending, (state) => {
        state.tenantSync.isLoadingSettings = true;
        state.tenantSync.error = null;
      })
      .addCase(fetchSyncSettings.fulfilled, (state, action) => {
        state.tenantSync.isLoadingSettings = false;
        state.tenantSync.frequency = action.payload.default_sync_frequency || 'daily';
        state.tenantSync.lastTenantSyncAt = action.payload.last_tenant_sync_at;
        state.tenantSync.lastTenantSyncStatus = action.payload.last_tenant_sync_status;
      })
      .addCase(fetchSyncSettings.rejected, (state, action) => {
        state.tenantSync.isLoadingSettings = false;
        state.tenantSync.error = action.payload;
      })
      // updateSyncFrequency
      .addCase(updateSyncFrequency.pending, (state) => {
        state.tenantSync.isUpdatingFrequency = true;
        state.tenantSync.error = null;
      })
      .addCase(updateSyncFrequency.fulfilled, (state, action) => {
        state.tenantSync.isUpdatingFrequency = false;
        state.tenantSync.frequency = action.payload.frequency;
      })
      .addCase(updateSyncFrequency.rejected, (state, action) => {
        state.tenantSync.isUpdatingFrequency = false;
        state.tenantSync.error = action.payload;
      })
      // triggerSyncNow
      .addCase(triggerSyncNow.pending, (state) => {
        state.tenantSync.isTriggeringSyncNow = true;
        state.tenantSync.error = null;
        state.tenantSync.syncNowStatus = null;
      })
      .addCase(triggerSyncNow.fulfilled, (state, action) => {
        state.tenantSync.isTriggeringSyncNow = false;
        state.tenantSync.syncNowStatus = action.payload.status;
        state.tenantSync.syncNowWorkflowId = action.payload.workflow_id;
        // If started, also set the active workflow in syncConfig
        if (action.payload.status === 'started') {
          state.syncConfig.activeWorkflow = {
            workflow_id: action.payload.workflow_id,
            status: 'running',
            progress_percent: 0,
            current_step: 'Starting sync...',
            objects_processed: 0,
            objects_total: 0,
            started_at: new Date().toISOString(),
          };
        }
      })
      .addCase(triggerSyncNow.rejected, (state, action) => {
        state.tenantSync.isTriggeringSyncNow = false;
        // Check if this is an auth_required error
        if (action.payload?.code === 'auth_required') {
          state.tenantSync.authRequired = {
            source_type: action.payload.source_type,
            source_name: action.payload.source_name,
            data_source_id: action.payload.data_source_id,
            auth_action: action.payload.auth_action,
          };
          state.tenantSync.error = null;
        } else {
          state.tenantSync.error = action.payload?.message || action.payload;
        }
      })
      // fetchFullSyncStatus
      .addCase(fetchFullSyncStatus.pending, (state) => {
        // Only set loading on first load, not on polling
        if (state.initialLoadStatus.fullSyncStatus === 'pending') {
          state.initialLoadStatus.fullSyncStatus = 'loading';
          state.initialLoadStatus.fullSyncStatusError = null;
        }
      })
      .addCase(fetchFullSyncStatus.fulfilled, (state, action) => {
        state.tenantSync.frequency = action.payload.default_sync_frequency || 'daily';
        state.tenantSync.lastTenantSyncAt = action.payload.last_tenant_sync_at;
        state.tenantSync.lastTenantSyncStatus = action.payload.last_tenant_sync_status;
        state.initialLoadStatus.fullSyncStatus = 'success';
        // Check if all initial loads are complete
        if (state.initialLoadStatus.fields !== 'pending' && state.initialLoadStatus.fields !== 'loading' &&
            state.initialLoadStatus.syncStatus !== 'pending' && state.initialLoadStatus.syncStatus !== 'loading') {
          state.initialLoadComplete = true;
        }
        // Merge last_sync_at and record_count from full-status into syncConfig.objects
        // (the cache may be stale; full-status reads directly from the DB)
        if (action.payload.objects?.length > 0) {
          const statusByName = {};
          for (const obj of action.payload.objects) {
            statusByName[obj.object_name] = obj;
          }
          state.syncConfig.objects = state.syncConfig.objects.map(obj => {
            const statusObj = statusByName[obj.name];
            if (statusObj) {
              return {
                ...obj,
                last_sync_at: statusObj.last_sync_at ?? obj.last_sync_at,
                record_count: statusObj.record_count ?? obj.record_count,
                is_synced: statusObj.is_synced ?? obj.is_synced,
              };
            }
            return obj;
          });
        }
        // Update active workflow if there's one running; clear if backend reports none
        if (action.payload.active_workflow) {
          state.syncConfig.activeWorkflow = {
            workflow_id: action.payload.active_workflow.workflow_id,
            status: action.payload.active_workflow.status,
            current_step: action.payload.active_workflow.current_step,
            current_object: action.payload.active_workflow.current_object,
            objects_processed: action.payload.active_workflow.objects_processed,
            objects_total: action.payload.active_workflow.objects_total,
            records_processed: action.payload.active_workflow.records_processed,
          };
        } else {
          state.syncConfig.activeWorkflow = null;
        }
      })
      .addCase(fetchFullSyncStatus.rejected, (state, action) => {
        state.initialLoadStatus.fullSyncStatus = 'error';
        state.initialLoadStatus.fullSyncStatusError = action.payload || 'Failed to load sync status';
        // Mark complete even on error
        state.initialLoadComplete = true;
      })
      // Builder V2 relationship thunks
      .addCase(fetchBuilderLookups.pending, (state) => {
        state.builderRelationships.isLoading = true;
        state.builderRelationships.error = null;
      })
      .addCase(fetchBuilderLookups.fulfilled, (state, action) => {
        state.builderRelationships.isLoading = false;
        state.builderRelationships.lookup = action.payload.lookups;
      })
      .addCase(fetchBuilderLookups.rejected, (state, action) => {
        state.builderRelationships.isLoading = false;
        state.builderRelationships.error = action.payload;
      })
      .addCase(fetchBuilderChildren.pending, (state) => {
        state.builderRelationships.isLoading = true;
        state.builderRelationships.error = null;
      })
      .addCase(fetchBuilderChildren.fulfilled, (state, action) => {
        state.builderRelationships.isLoading = false;
        state.builderRelationships.children = action.payload.children;
      })
      .addCase(fetchBuilderChildren.rejected, (state, action) => {
        state.builderRelationships.isLoading = false;
        state.builderRelationships.error = action.payload;
      });
  },
});

export const {
  setActiveTab,
  setSelectedObject,
  setSearchQuery,
  clearError,
  resetInitialLoad,
  setQueryText,
  clearQueryResult,
  clearActiveSyncWorkflow,
  setActiveTopicJob,
  clearActiveTopicJob,
  clearBuilderRelationships,
  // Sync config actions
  toggleObjectSync,
  revertObjectSync,
  updatePendingFields,
  discardSyncChanges,
  setSyncConfigSelectedObject,
  clearSyncConfigWorkflow,
  clearSyncConfigError,
  clearAuthRequired,
} = analyticsSlice.actions;
export default analyticsSlice.reducer;
